import { Navigate, Outlet } from 'react-router-dom';
import { useAccessStore } from '@sphere/auth/data-access';
import { useAdminStore, useAdminStoreHydrated } from '@sphere/shared/data-access/store';
import { useKeycloak } from '@sphere/auth/data-access';

/**
 * Guard qui force la sélection d'un établissement pour les administrateurs
 * Si l'utilisateur est admin et n'a pas de contexte établissement, redirige vers la page de sélection
 *
 * ⚠️ SÉCURITÉ : Ce guard vérifie les rôles côté frontend uniquement pour l'UX.
 * Le backend DOIT TOUJOURS valider les rôles et l'établissement dans les requêtes API.
 *
 * Pour améliorer la sécurité, on vérifie aussi directement depuis le token JWT signé
 * en plus du store (qui peut être manipulé côté client).
 */
export function RequireEstablishment() {
  const { tokenParsed, userInfo } = useKeycloak();
  const isAdminFromStore = useAccessStore((state) => state.isAdmin());
  const hasHydrated = useAdminStoreHydrated();
  const currentEstablishment = useAdminStore(
    (state) => state.currentEstablishment
  );

  // Attendre que le store soit hydraté depuis localStorage avant de prendre une décision
  // Sinon on risque de rediriger avant que le store ait chargé l'établissement sauvegardé
  if (!hasHydrated) {
    // Afficher un état de chargement minimal pendant l'hydratation
    return null;
  }

  // Vérifier les rôles depuis le token JWT signé (source de vérité non falsifiable)
  // C'est une double vérification pour éviter les manipulations du store
  const tokenRoles = tokenParsed?.realm_access?.roles || [];
  const userRoles = userInfo?.realmRoles || tokenRoles;
  const adminRoles = [
    'admin',
    'administrateur',
    'realm-admin',
    'Admin',
    'ADMIN',
    'Administrateur',
  ];
  const isAdminFromToken = adminRoles.some((role) => userRoles.includes(role));

  // Utiliser la vérification depuis le token en priorité (plus sécurisé)
  // Fallback sur le store si le token n'est pas disponible
  const isAdmin = isAdminFromToken || isAdminFromStore;

  // Si c'est un admin et qu'aucun établissement n'est sélectionné
  if (isAdmin && !currentEstablishment) {
    return <Navigate to="/admin/select-establishment" replace />;
  }

  // Sinon (non-admin ou admin avec établissement), on laisse passer
  // Note: Le backend validera toujours les permissions réelles dans les requêtes API
  return <Outlet />;
}
